import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// AS-S3 — search-driven combobox that replaces the previous list-style
// EmployeePicker. Behaviour:
//   • Single text input. As the user types, the dropdown filters the
//     `employees` list by case-insensitive substring on name / email /
//     position. No scoring, no fuzzy match, no suggestions.
//   • Optional `departmentBlacklist` is a *secondary* filter applied after
//     the substring match — names typed into the box can still surface
//     blacklisted matches by name, but the dropdown for an empty query
//     hides them. This is a soft filter, not a hard gate.
//   • Keyboard: ArrowDown / ArrowUp move the highlight, Enter selects the
//     highlighted item, Escape closes the dropdown.
//   • ✕ clear button resets selection and refocuses the input.
//   • Click outside closes the dropdown (the input is not cleared).
//
// The component is intentionally uncontrolled for the search text but
// controlled for the selected employee via `value` / `onChange`.

const matchesQuery = (emp, q) => {
  if (!q) return true;
  const haystack = `${emp.fullName || ''} ${emp.email || ''} ${emp.position || ''}`.toLowerCase();
  return haystack.includes(q);
};

const EmployeeCombobox = ({
  employees = [],
  value = null,
  onChange,
  departmentBlacklist = [],
  placeholder = 'Search employee…',
  testIdPrefix = 'employee-combobox',
  autoFocus = false,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const blacklistSet = useMemo(
    () => new Set((departmentBlacklist || []).map((d) => (d || '').toLowerCase())),
    [departmentBlacklist]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = employees
      .filter((e) => matchesQuery(e, q))
      .filter((e) => {
        // Empty query: hide blacklisted departments entirely.
        // Non-empty query: respect the user's typing — they can still match
        // by name even if the department is in the blacklist.
        if (q) return true;
        const dept = (e.department || '').toLowerCase();
        return !blacklistSet.has(dept);
      });
    return list;
  }, [employees, query, blacklistSet]);

  // Reset highlight whenever the filtered set changes.
  useEffect(() => { setHighlight(0); }, [filtered.length, query]);

  // Click outside closes the dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const select = useCallback((emp) => {
    onChange?.(emp);
    setQuery(emp ? (emp.fullName || emp.email || '') : '');
    setOpen(false);
  }, [onChange]);

  const clear = () => {
    onChange?.(null);
    setQuery('');
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const inputValue = value
    ? (value.fullName || value.email || '')
    : query;

  return (
    <div className="as-combobox" ref={wrapRef} data-testid={`${testIdPrefix}-root`}>
      <div className="as-combobox-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="as-combobox-input"
          placeholder={placeholder}
          value={inputValue}
          autoFocus={autoFocus}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            // If the user starts typing while a value is selected, clear it.
            if (value) onChange?.(null);
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          data-testid={`${testIdPrefix}-input`}
        />
        {(value || query) && (
          <button
            type="button"
            className="as-combobox-clear"
            onClick={clear}
            aria-label="Clear selection"
            data-testid={`${testIdPrefix}-clear`}
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div className="as-combobox-dropdown" role="listbox" data-testid={`${testIdPrefix}-dropdown`}>
          {filtered.length === 0 ? (
            <div className="as-combobox-empty" data-testid={`${testIdPrefix}-empty`}>
              No employees match.
            </div>
          ) : (
            filtered.map((emp, idx) => (
              <div
                key={emp.id}
                role="option"
                aria-selected={idx === highlight}
                className={`as-combobox-option${idx === highlight ? ' is-highlight' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); select(emp); }}
                onMouseEnter={() => setHighlight(idx)}
                data-testid={`${testIdPrefix}-opt-${emp.id}`}
              >
                <span className="as-combobox-name">{emp.fullName || emp.email}</span>
                {(emp.position || emp.department) && (
                  <span className="as-combobox-meta">
                    {[emp.position, emp.department].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeCombobox;
